(ns app.front.view.audit-log.list
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.party.model :as party-model]
            [app.domain.workspace-notification.model :as workspace-notification-model]
            [app.front.entities :as entities]
            [app.front.ui.section :as section]
            [app.front.ui.table :as ui-table]
            [app.front.util :refer [for-all]]
            [app.front.view.audit-log.forms :as forms]
            [app.front.view.audit-log.forms.subs :as forms-subs]
            [app.front.view.workspace.route-subs :as ws-route-subs]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.user.model :as user-model]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

(rf/reg-event-fx ::show-audit-log-view
  (fn [_ [_ process-target]]
    {:route/push {:route  :view/audit-log
                  :params {:workspace-id (-> process-target :entity/parent :workspace/id)}}}))


(rf/reg-event-fx ::fetch-ws-notifs
  (fn [_ [_ ws-id]]
    {:dispatch [:comm/command-monitored
                {:command    :workspace-notification/get
                 :method     :get
                 :data       {:workspace-id ws-id}
                 :on-success [::entities/merge-entity-db]}]}))


(rf/reg-event-fx ::fetch-audit-events
  (fn [_ [_ ws-id]]
    {:dispatch [:comm/command-monitored
                {:command    :workspace/audit-events
                 :method     :get
                 :data       {:workspace-id ws-id}
                 :on-success [::entities/merge-entity-db]}]}))


(rf/reg-sub ::entities-sorted
  (fn [[_ entity-type ws-db-id]]
    (rf/subscribe [::entities/entities {:entity/type   entity-type
                                        :entity/parent ws-db-id}]))
  (fn [entities _]
    (sort-by :entity/created entities)))


(rf/reg-event-fx ::fetch-email-logs
  (fn [_ [_ ws-id]]
    {:dispatch [:comm/command-monitored
                {:command    :workspace/email-logs
                 :method     :get
                 :data       {:workspace-id ws-id}
                 :on-success [::fetch-email-logs.success ws-id]}]}))


(rf/reg-event-db ::fetch-email-logs.success
  (fn [db [_ ws-id _status email-logs]]
    (assoc-in db [::email-logs ws-id] email-logs)))


(rf/reg-sub ::email-logs :-> ::email-logs)


(rf/reg-sub ::workspace-emails
  :<- [::email-logs]
  :<- [::ws-subs/workspace-id]
  (fn [[email-logs ws-id] _]
    (->> ws-id
         (get email-logs)
         (sort-by :entity/created))))


(defn format-property [{:property/keys [id integration-id primary-address]}]
  (->> [(:address/full-address primary-address)
        id
        (when integration-id
          (str (<sub [::ws-subs/backend-system-name]) " ID " integration-id))]
       (remove nil?)
       (str/join " / ")))


(defn- single-related-entity [{:keys [lang tr]} entity]
  (let [tr-silent (<sub [:tr/quiet])]
    [:div.mb-1
     (case (:entity/type entity)
       :entity.type/property (format-property entity)
       :entity.type/verdict (tr [:verdict/permit-number-template] [(:verdict/municipal-permit-number entity)])
       :entity.type/form-field (tr [:form/field-name] [(tr-silent [(:form.field/id entity)])])
       :entity.type/form (tr [:form/name] [(-> entity :form/schema :form.schema/name)])
       :entity.type/party (or (party-model/unchecked-user-party-email entity)
                              (party-model/simple-name entity lang))
       (tr [(:entity/type entity)]))]))

(defmulti related-entity-col (fn [_ctx {:audit-event/keys [type]}] type))

(defn- default-related-entity-col [{:keys [lang tr]} event]
  [:div.flex.flex-wrap
   (interpose
     [:div.pr-1 ";"]
     (for [ent (->> (:audit-event/related-entities event)
                    ;; remove deleted entities
                    (remove (comp nil? :entity/type)))]
       ^{:key (:db/id ent)}
       [single-related-entity (util/mmap lang tr) ent]))])

(defmethod related-entity-col :default [ctx event]
  [default-related-entity-col ctx event])

(defmethod related-entity-col :audit-event.type/re-issue-permit [{:keys [tr] :as ctx} event]
  [:div
   (if (:entity/file-object-key event)
     (cc/link {:href (str "/api/command/audit-event/download-file?audit-event-id=" (:db/id event))}
              (tr [:audit-log/download-old-verdict]))
     [default-related-entity-col ctx event])])

(defmethod related-entity-col :audit-event.type/ws-config-path-change [{:keys [tr]} _]
  (tr [:entity.type/workspace]))

(defmethod related-entity-col :audit-event.type/municipal-case-id-change [{:keys [tr]} _]
  (tr [:entity.type/workspace]))

(defmulti event-details (fn [_ctx {:audit-event/keys [type]}] type))

(defmethod event-details :default [_ _] nil)

(defmethod event-details :audit-event.type/condition-due-date-change [_ {:audit-event/keys [additional-information]}]
  additional-information)

(defmethod event-details :audit-event.type/condition-attachment-added [_ {:audit-event/keys [additional-information]}]
  additional-information)

(defmethod event-details :audit-event.type/condition-attachment-deleted [_ {:audit-event/keys [additional-information]}]
  additional-information)

(defmethod event-details :audit-event.type/review-due-date-change [_ {:audit-event/keys [additional-information]}]
  additional-information)

(defmethod event-details :audit-event.type/domain-state-change [{:keys [tr]} {:audit-event/keys [new-domain-state]}]
  (when new-domain-state (tr [new-domain-state])))

(defmethod event-details :audit-event.type/issued-expired-licence [_ {:audit-event/keys [additional-information]}]
  additional-information)

(defmethod event-details :audit-event.type/edit-checklist-item-note [_ {:audit-event/keys [additional-information]}]
  additional-information)

(defn- config-path-change-details
  [{:keys [tr lang workspace]} {:audit-event/keys [old-group-configs new-group-configs]}]
  [:div
   (tr [:audit-log/ws-config-path-changed-from])
   [:br]
   [:span (str (config-model/leaf-configs-to-path-string lang (:workspace/permit-type workspace) old-group-configs)
               " " (tr [:audit-log/changed-to]))]
   [:br]
   [:span.bold (config-model/leaf-configs-to-path-string lang (:workspace/permit-type workspace) new-group-configs)]])

(defmethod event-details :audit-event.type/ws-config-path-change [ctx audit-event]
  (config-path-change-details ctx audit-event))

(defmethod event-details :audit-event.type/admin-changed-config-path [ctx audit-event]
  (config-path-change-details ctx audit-event))

(defmethod event-details :audit-event.type/municipal-case-id-change
  [{:keys [tr]} {:audit-event/keys [old-municipal-case-id new-municipal-case-id]}]
  (if new-municipal-case-id
    [:div
     (->> [(tr [:audit-log/case-id-changed-from])
           old-municipal-case-id
           (tr [:audit-log/changed-to])]
          (str/join " "))
     " "
     [:span.bold new-municipal-case-id]]
    [:div (tr [:audit-log/case-id-removed]
              [old-municipal-case-id])]))

(defn- notifications-list [tr format-date-time]
  (let [loading?      (<sub [:comm/command-processing? :workspace-notification/get])
        ws-db-id      (<sub [::ws-subs/workspace-entity-id])
        notifications (<sub [::entities-sorted :entity.type/workspace-notification ws-db-id])]
    [:<>
     [:h3.mb-4 (tr [:notifications/notifications])]
     (cond
       loading?
       [section/section-skeleton]

       (empty? notifications)
       (tr [:notifications/no-notifications])

       :else
       [ui-table/modern-responsive-table
        {:items         notifications
         :tr            tr
         :class         "mb-4"
         :cols          [[(tr [:date/date])
                          (fn [notification]
                            (-> notification
                                :entity/created
                                format-date-time))]
                         [(tr [:notifications/type])
                          (fn [notification]
                            (if (contains? (set workspace-notification-model/dormancy-notification-types)
                                           (:workspace-notification/type notification))
                              (tr [:notifications/dormancy-email])
                              (tr [(:workspace-notification/type notification)])))]
                         [(tr [:notifications/source])
                          (fn [notification]
                            (or (some-> notification
                                        :workspace-notification/source
                                        user-model/full-name)
                                (tr [:system])))]
                         [(tr [:notifications/target])
                          (fn [notification]
                            [:div.flex.flex-wrap
                             (map (fn [item]
                                    [:div.flex-1 (or (:party/email item)
                                                     (:user/email item))])
                                  (:workspace-notification/parties notification))])]
                         [(tr [:notifications/subject])
                          :workspace-notification/subject]]
         :expanded-func (fn [notification]
                          (when-some [body (:workspace-notification/body notification)]
                            [:iframe.p-4 {:width  "100%"
                                          :height "900px"
                                          :srcdoc body}]))}])]))

(defn- audit-events-list [tr format-date-time]
  (let [lang         (<sub [:language])
        workspace    (<sub [::ws-subs/workspace])
        loading?     (<sub [:comm/command-processing? :workspace/audit-events])
        audit-events (<sub [::entities-sorted :entity.type/audit-event (:db/id workspace)])]
    [:<>
     [:h3.mt-6.mb-4 (tr [:audit-log/audit-events])]
     (cond
       loading?
       [section/section-skeleton]

       (empty? audit-events)
       (tr [:audit-log/no-events])

       :else
       [ui-table/modern-responsive-table
        {:items audit-events
         :tr    tr
         :class "mb-4"
         :cols  [[(tr [:date/date])
                  (fn [event]
                    (-> event :entity/created format-date-time))]
                 [(tr [:audit-event/type])
                  (fn [event]
                    (tr [(:audit-event/type event)]))]
                 [(tr [:user/user])
                  (fn [event]
                    (or (some-> event
                                :entity/creator
                                user-model/full-name)
                        (tr [:system])))]
                 [(tr [:audit-log/details])
                  (fn [event]
                    [event-details (util/mmap tr lang workspace) event])]
                 [(tr [:audit-event/related-entities])
                  (fn [event]
                    [related-entity-col (util/mmap lang tr) event])]]}])]))

(defn audit-log-section [tr format-date-time]
  [section/section
   "audit-log-section"
   {:tr tr}
   :notifications/audit-log
   [:div
    [notifications-list tr format-date-time]
    [audit-events-list tr format-date-time]]])

(defn- email-log-section [tr format-date-time]
  (let [loading? (<sub [:comm/command-processing? :workspace/email-logs])
        emails   (<sub [::workspace-emails])]
    [section/section
     "email-log-section"
     {:tr tr}
     :audit-log/email-log
     [:div
      [:h3.mb-4 (tr [:audit-log/emails])]
      (cond
        loading?
        [section/section-skeleton]

        (empty? emails)
        (tr [:audit-log/no-emails])

        :else
        [ui-table/modern-responsive-table
         {:items         emails
          :tr            tr
          :get-status    (fn [email]
                           (case (:email-log/delivery-status email)
                             :email-log.delivery-status/delivered :ok
                             :email-log.delivery-status/temporary-failure :warning
                             :email-log.delivery-status/permanent-failure :critical
                             :attention))
          :cols          [[(tr [:email-log/date-sent])
                           (fn [email]
                             (-> email :entity/created format-date-time))
                           {:max-width "11rem"}]
                          [(tr [:email-log/delivery-status])
                           (fn [email]
                             (tr [(or (:email-log/delivery-status email)
                                      :email-log.delivery-status/unknown)]))
                           {:max-width "10rem"}]
                          [(tr [:email-log/to])
                           :address
                           {:word-break "break-all"
                            :max-width  "25rem"}]
                          [(tr [:email-log/subject])
                           :subject]]
          :expanded-func (fn [email]
                           [:div.my-4.ml-8
                            [:div.bold.text-xs.pb-2
                             (tr [:email-log/delivery-details])]
                            [:div.text-sm
                             (or (->> [(:email-log/delivery-status-code email)
                                       (:email-log/delivery-status-message email)]
                                      (remove nil?)
                                      (str/join " ")
                                      str-util/blank->nil)
                                 (tr [:email-log/delivery-details-unavailable]))]])}])]]))

(defn legacy-view []
  (let [workspace        (<sub [::ws-subs/workspace])
        lang             (<sub [:language])
        tr               (<sub [:tr])
        notifications    (<sub [::entities/entities {:entity/type   :entity.type/workspace-notification
                                                     :entity/parent (:db/id workspace)}])
        audit-events     (<sub [::entities/entities {:entity/type   :entity.type/audit-event
                                                     :entity/parent (:db/id workspace)}])
        format-date-time (<sub [::date-util/date-time-fn])]
    [section/section
     "audit-log-section"
     {:tr tr}
     :notifications/audit-log
     [:div
      [:h3.mb-4 (tr [:notifications/notifications])]
      (if (seq notifications)
        #_{:clj-kondo/ignore [:deprecated-var]}
        [ui-table/responsive-table
         {:tr          tr
          :class       "mb-4"
          :expandable? true}
         [{:title (tr [:date/date])}
          {:title (tr [:notifications/type])}
          {:title (tr [:notifications/source])}
          {:title (tr [:notifications/target])}
          {:title (tr [:notifications/subject])}]
         (for-all [notification notifications]
           {:key              (:db/id notification)
            :expanded-content (when (:workspace-notification/body notification)
                                [:iframe.p-4 {:width  "100%"
                                              :height "900px"
                                              :srcdoc (:workspace-notification/body notification)}])
            :cols             [{:content [:div (format-date-time (:entity/created notification))]}
                               {:content (if (contains? (set workspace-notification-model/dormancy-notification-types)
                                                        (:workspace-notification/type notification))
                                           (tr [:notifications/dormancy-email])
                                           (tr [(:workspace-notification/type notification)]))}
                               {:content (if (:workspace-notification/source notification)
                                           (-> notification
                                               :workspace-notification/source
                                               user-model/full-name)
                                           (tr [:system]))}
                               {:content [:div.flex.flex-wrap
                                          (map (fn [item]
                                                 [:div.flex-1 (or (:party/email item)
                                                                  (:user/email item))])
                                               (:workspace-notification/parties notification))]}
                               {:content (:workspace-notification/subject notification)}]})]
        (tr [:notifications/no-notifications]))
      [:h3.mt-6.mb-4 (tr [:audit-log/audit-events])]
      (if (seq audit-events)
        #_{:clj-kondo/ignore [:deprecated-var]}
        [ui-table/responsive-table
         {:tr    tr
          :class "mb-4"}
         [{:title (tr [:date/date])
           :width "180px"
           :flex  0}
          {:title (tr [:audit-event/type])
           :flex  0.5}
          {:title     (tr [:user/user])
           :flex      0.5
           :max-width "250px"}
          {:title (tr [:audit-log/details])}
          {:title (tr [:audit-event/related-entities])}]
         (for-all [event (->> audit-events
                              (sort-by :entity/created))]
           {:key  (:db/id event)
            :cols [{:content [:div (format-date-time (:entity/created event))]}
                   {:content (tr [(:audit-event/type event)])}
                   {:content (or (some-> event
                                         :entity/creator
                                         user-model/full-name)
                                 (tr [:system]))}
                   {:content [event-details (util/mmap tr lang workspace) event]}
                   {:content [related-entity-col (util/mmap lang tr) event]}]})]
        (tr [:audit-log/no-events]))]]))

(defn view [_]
  [:div.cloudpermit-page
   (if-not (<sub [::ff/enabled? :feature-flag.id/emails-in-audit-log])
     [legacy-view]
     (let [tr               (<sub [::ws-subs/tr])
           format-date-time (<sub [::date-util/date-time-fn])]
       [:<>
        [audit-log-section tr format-date-time]
        [email-log-section tr format-date-time]]))
   (when (<sub [::ff/enabled? :feature-flag.id/form-data-audit-log])
     [forms/view])])


(def route
  {:name        :view/audit-log
   :view        view
   :view-sub    [::ws-route-subs/workspace-sub-page-view-data]
   :controllers [{:parameters {:path [:workspace-id]}
                  :start      (fn [{{:keys [workspace-id]} :path}]
                                (>evt [::fetch-ws-notifs workspace-id])
                                (>evt [::fetch-audit-events workspace-id])
                                (>evt [::fetch-email-logs workspace-id])
                                (>evt [::forms-subs/start workspace-id]))
                  :stop       (fn [_] (>evt [::forms-subs/stop]))}]})
